import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../../lib/api'
import type { Doctor, PaginatedResponse } from '../../types'

interface DoctorSearchSelectProps {
  value: string
  onChange: (doctorId: string, doctor?: Doctor) => void
  onBlur?: () => void
  error?: string
  label?: string
  required?: boolean
  placeholder?: string
}

/**
 * Unified Doctor Search Select component with fuzzy search.
 * Displays doctor name, phone number, and clinic name.
 * Supports search by:
 * - Doctor name
 * - Clinic name
 * - Phone number
 */
export default function DoctorSearchSelect({
  value,
  onChange,
  onBlur,
  error,
  label = 'Doctor / Client',
  required = false,
  placeholder = 'Search by name, clinic, or phone...',
}: DoctorSearchSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['doctors', 'search-select'],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Doctor>>('/doctors?per_page=100')
      return data.data || []
    },
    staleTime: 30000,
  })

  const doctors: Doctor[] = data || []
  const selectedDoctor = doctors.find(d => d.id === value)

  // Fuzzy filter doctors based on search
  const filteredDoctors = doctors.filter((d) => {
    if (!search) return true
    const searchLower = search.toLowerCase().trim()

    // Search in name
    if (d.name.toLowerCase().includes(searchLower)) return true

    // Search in clinic name
    if (d.clinicName && d.clinicName.toLowerCase().includes(searchLower)) return true

    // Search in phone (strip non-digits for better matching)
    const searchDigits = searchLower.replace(/\D/g, '')
    const phoneDigits = d.phone.replace(/\D/g, '')
    if (searchDigits && phoneDigits.includes(searchDigits)) return true
    if (d.phone.includes(searchLower)) return true

    // Search in display ID / code
    if (d.displayId && d.displayId.toLowerCase().includes(searchLower)) return true

    return false
  }).slice(0, 10)

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
        onBlur?.()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onBlur])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement
      if (el) el.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const handleSelect = (doctor: Doctor) => {
    onChange(doctor.id, doctor)
    setIsOpen(false)
    setSearch('')
    setHighlightedIndex(-1)
  }

  const handleClear = () => {
    onChange('', undefined)
    setSearch('')
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault()
      setIsOpen(true)
      setTimeout(() => inputRef.current?.focus(), 0)
      return
    }

    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev < filteredDoctors.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredDoctors.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredDoctors[highlightedIndex]) {
          handleSelect(filteredDoctors[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearch('')
        setHighlightedIndex(-1)
        break
    }
  }

  const handleOpen = () => {
    setIsOpen(true)
    setHighlightedIndex(-1)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}{required && ' *'}
        </label>
      )}

      {/* Selected Doctor Display / Trigger */}
      {selectedDoctor && !isOpen ? (
        <div
          onClick={handleOpen}
          className={`w-full flex items-center justify-between px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-700 cursor-pointer transition-all hover:border-primary-400 ${
            error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <i className="fas fa-user-md text-primary-600 dark:text-primary-400"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {selectedDoctor.name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                <i className="fas fa-phone text-xs mr-1"></i>
                {selectedDoctor.phone}
                {selectedDoctor.clinicName && (
                  <>
                    <span className="mx-2">•</span>
                    <i className="fas fa-hospital text-xs mr-1"></i>
                    {selectedDoctor.clinicName}
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
          className={`w-full flex items-center justify-between px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <i className="fas fa-user-md text-gray-400 text-sm"></i>
            <span className="text-gray-400 dark:text-gray-500 truncate">{placeholder}</span>
          </div>
          <i className={`fas fa-chevron-down text-gray-400 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              {isLoading ? (
                <i className="fas fa-spinner fa-spin absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              ) : (
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              )}
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setHighlightedIndex(-1)
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoComplete="off"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              <i className="fas fa-lightbulb mr-1"></i>
              Search by doctor name, clinic name, or phone number
            </p>
          </div>

          {/* Options List */}
          <div ref={listRef} className="max-h-72 overflow-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center">
                <i className="fas fa-spinner fa-spin text-gray-400 text-xl"></i>
                <p className="text-sm text-gray-500 mt-2">Loading doctors...</p>
              </div>
            ) : filteredDoctors.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <i className="fas fa-user-md text-gray-300 dark:text-gray-600 text-3xl"></i>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {search ? `No doctors found for "${search}"` : 'No doctors available'}
                </p>
              </div>
            ) : (
              filteredDoctors.map((doctor, index) => (
                <div
                  key={doctor.id}
                  onClick={() => handleSelect(doctor)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    highlightedIndex === index
                      ? 'bg-primary-50 dark:bg-primary-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  } ${doctor.id === value ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center">
                    <i className="fas fa-user-md text-gray-500 dark:text-gray-400"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {doctor.name}
                      </p>
                      {doctor.id === value && (
                        <i className="fas fa-check text-primary-600 dark:text-primary-400 text-xs"></i>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <span className="flex items-center gap-1">
                        <i className="fas fa-phone"></i>
                        {doctor.phone}
                      </span>
                      {doctor.clinicName && (
                        <span className="flex items-center gap-1 truncate">
                          <i className="fas fa-hospital"></i>
                          {doctor.clinicName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
          <i className="fas fa-exclamation-circle text-xs"></i>
          {error}
        </p>
      )}
    </div>
  )
}
